/**
 * Pure helpers for TOCSidebar — extracted so the printed-number display
 * and collapse-seeding logic can be unit-tested without mounting the
 * component tree.
 */
import type { TOCEntry } from '../../types/viewer';

const SEG_CONTAINER = /^SEG\d+$/i;
const SEG_PREFIXED = /^SEG\d+/i;
const RESERVED_UNNUMBERED = /(?:^|\.)UN:\d+$/i;

/** Internal identity for a title-only section; never a printed number. */
export function isReservedSectionNumber(sectionNumber: string | null): boolean {
  return Boolean(sectionNumber && RESERVED_UNNUMBERED.test(sectionNumber));
}

/** Human-readable evidence label that never exposes reserved identities. */
export function sectionEvidenceLabel(
  sectionNumber: string | null,
  sectionTitle: string,
): string {
  if (!sectionNumber || isReservedSectionNumber(sectionNumber)) return sectionTitle;
  return sectionTitle.startsWith(sectionNumber)
    ? sectionTitle
    : `${sectionNumber} ${sectionTitle}`;
}

/**
 * Extract the short numeric section number from a prefixed section number.
 * e.g., "Clinical_Pharmacology_Review_3.2.1" → "3.2.1"
 *       "Medical_Review_1.1" → "1.1"
 *       "5.3" → "5.3"
 *
 * Accepts hyphenated numbers too (jp_pdf review_report uses the document's
 * own "2-1" / "2-2-1" numbering); dotted numbers ("14.1") and SBA prefixes
 * are unaffected since they carry no hyphens.
 */
export function extractShortNumber(sectionNumber: string | null): string {
  if (!sectionNumber) return '';
  const match = sectionNumber.match(/(\d+(?:[.\-]\d+)*)$/);
  return match?.[1] ?? sectionNumber;
}

/**
 * Printed number for display in the TOC row.
 *
 * - Blank for a bundle container row (canonical ``section_number`` is
 *   exactly ``SEG<N>``) — the container has no printed number of its own.
 * - Otherwise prefer the FDA/regulator-printed local number
 *   (``section_number_local``) when present.
 * - A bundle child with no local number (e.g. a title-only "unnamed"
 *   section, "SEG2.U1") passes through verbatim — ``extractShortNumber``'s
 *   trailing-digit regex would otherwise strip it down to a misleading
 *   guessed number ("1"). Never invent a printed number that isn't there.
 * - Fall back to ``extractShortNumber`` only for genuinely legacy/
 *   non-bundle rows (canonical number does not start with the ``SEG``
 *   composite prefix) — preserves existing display for multi-PDF-prefixed
 *   numbers like "Clinical_Pharmacology_Review_3.2.1" → "3.2.1".
 */
export function sectionDisplayNumber(
  entry: Pick<TOCEntry, 'section_number' | 'section_number_local'>,
): string {
  const canon = entry.section_number ?? '';
  if (SEG_CONTAINER.test(canon)) return '';
  if (
    isReservedSectionNumber(canon)
    || isReservedSectionNumber(entry.section_number_local ?? null)
  ) return '';
  if (entry.section_number_local) return entry.section_number_local;
  if (SEG_PREFIXED.test(canon)) return canon;
  return extractShortNumber(entry.section_number);
}

/**
 * Seed the initially-collapsed row-ID set at the TOP-MOST present depth
 * among rows that actually own children — never a hardcoded
 * ``level === 1`` (a bundle tree's containers sit at level 0), and never
 * a leaf (a row nothing points at via ``parent_section_id``).
 *
 * EU presentation anchors (``is_group_header``) are excluded: their
 * sections are governed by the GROUP banner, not the per-row chevron,
 * so seed-collapsing them would hide a group whose banner reads
 * "expanded".
 */
export function seedCollapsed(entries: TOCEntry[]): Set<number> {
  const out = new Set<number>();
  const parentIds = new Set(
    entries
      .map((e) => e.parent_section_id)
      .filter((id): id is number => id != null),
  );
  const parents = entries.filter((e) => !e.is_group_header && parentIds.has(e.id));
  if (!parents.length) return out;
  const topLevel = Math.min(...parents.map((e) => e.level));
  for (const e of parents) {
    if (e.level === topLevel) out.add(e.id);
  }
  return out;
}
